import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import moment from 'moment'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Rating,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import CorporateSplitPage from '../../../components/SplitPage/CorporateSplitPage'
import API from '../../../services/API'

// Public client feedback form behind an event feedback link
// (/event-feedback/:token). No login: the token is a 192-bit random,
// single-use secret with an expiry. The page shows only the event title and
// dates and the provider's name. Server: routers/eventPlanning/eventFeedbackRequests.js

const CLOSED_MESSAGES = {
  expired: {
    title: 'This link has expired',
    body: 'Feedback links only stay open for a limited time. If you would still like to leave feedback, please get in touch with the team that sent you the link and they can send a new one.',
  },
  used: {
    title: 'Feedback already received',
    body: 'This link has already been used to leave feedback. Thank you! If you would like to add anything, please contact the team directly.',
  },
  revoked: {
    title: 'This link is no longer active',
    body: 'The team has cancelled this feedback request. If you believe this is a mistake, please contact them directly.',
  },
  invalid: {
    title: "We couldn't recognise this link",
    body: 'Please check that you have opened the complete link from your email or message. If the problem continues, contact the team that sent it.',
  },
  throttled: {
    title: 'Too many attempts',
    body: 'Please wait a few minutes and try again.',
  },
}

const RATING_LABELS = {
  1: 'Poor',
  2: 'Below expectations',
  3: 'Good',
  4: 'Very good',
  5: 'Excellent',
}

const eventDates = (start, end) => {
  if (!start) return ''
  const s = moment(start)
  const e = end ? moment(end) : null
  if (!e || e.isSame(s, 'day')) return s.format('dddd Do MMMM YYYY')
  return `${s.format('Do MMMM')} – ${e.format('Do MMMM YYYY')}`
}

const RatingRow = ({ label, value, onChange, size = 'large', required }) => {
  const [hover, setHover] = useState(-1)
  const shown = hover !== -1 ? hover : value
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      justifyContent="space-between"
      gap={{ xs: 0.5, sm: 2 }}
    >
      <Typography fontWeight={size === 'large' ? 800 : 600}>
        {label}
        {required && ' *'}
      </Typography>
      <Stack direction="row" alignItems="center" gap={1.5}>
        <Rating
          value={value}
          size={size}
          onChange={(_e, next) => onChange(next)}
          onChangeActive={(_e, next) => setHover(next)}
        />
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 112 }}>
          {shown ? RATING_LABELS[shown] : 'Not rated'}
        </Typography>
      </Stack>
    </Stack>
  )
}

const EventFeedbackPublic = () => {
  const { token } = useParams()
  // 'checking' | 'closed' | 'form' | 'submitted'
  const [step, setStep] = useState('checking')
  const [state, setState] = useState(null)
  const [request, setRequest] = useState(null)

  const [respondentName, setRespondentName] = useState('')
  const [respondentRole, setRespondentRole] = useState('')
  const [overallRating, setOverallRating] = useState(null)
  const [ratings, setRatings] = useState({})
  const [bookAgain, setBookAgain] = useState(null)
  const [wentWell, setWentWell] = useState('')
  const [couldImprove, setCouldImprove] = useState('')
  const [testimonialConsent, setTestimonialConsent] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [problems, setProblems] = useState([])

  useEffect(() => {
    setStep('checking')
    API.get(`/event-feedback/${token}`, { skipGlobalErrorHandler: true })
      .then((res) => {
        const nextState = res.status === 200 ? res.data?.state : res.data?.state || 'invalid'
        setState(nextState)
        if (nextState === 'open') {
          setRequest(res.data)
          setRespondentName(res.data.recipientName ?? '')
          setStep('form')
        } else {
          setStep('closed')
        }
      })
      .catch(() => {
        setState('invalid')
        setStep('closed')
      })
  }, [token])

  const canSubmit = respondentName.trim() && overallRating && !submitting

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!canSubmit) return
    setSubmitting(true)
    setProblems([])
    API.post(
      `/event-feedback/${token}`,
      {
        respondentName: respondentName.trim(),
        respondentRole: respondentRole.trim(),
        overallRating,
        ratings,
        bookAgain,
        wentWell: wentWell.trim(),
        couldImprove: couldImprove.trim(),
        testimonialConsent,
      },
      { skipGlobalErrorHandler: true },
    )
      .then((res) => {
        if (res.status === 200) {
          setStep('submitted')
        } else if (res.status === 400 && res.data?.problems) {
          setProblems(res.data.problems)
        } else if (res.status === 409 || res.status === 429) {
          setState(res.data?.state || 'invalid')
          setStep('closed')
        } else {
          setProblems(['Something went wrong sending your feedback. Please try again.'])
        }
      })
      .catch(() => setProblems(['Something went wrong sending your feedback. Please try again.']))
      .finally(() => setSubmitting(false))
  }

  const closed = CLOSED_MESSAGES[state] ?? CLOSED_MESSAGES.invalid
  const orgName = request?.orgName || 'us'
  const hasComments = wentWell.trim() || couldImprove.trim()

  return (
    <CorporateSplitPage pageTitle="Event Feedback">
      {step === 'checking' ? (
        <Alert severity="info" icon={false}>
          Checking your feedback link…
        </Alert>
      ) : step === 'closed' ? (
        <Alert severity="warning">
          <AlertTitle>{closed.title}</AlertTitle>
          {closed.body}
        </Alert>
      ) : step === 'submitted' ? (
        <Alert severity="success">
          <AlertTitle>Thank you!</AlertTitle>
          Your feedback has been sent to {request?.orgName || 'the team'}. We really appreciate
          you taking the time. You can now close this page.
        </Alert>
      ) : (
        <Stack gap={3} component="form" onSubmit={handleSubmit}>
          <Box>
            <Typography variant="title3" component="h1">
              How did {orgName} do?
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              <strong>{request?.eventTitle || 'Your event'}</strong>
              {request?.eventStart && ` · ${eventDates(request.eventStart, request.eventEnd)}`}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              It takes about two minutes and you don't need an account. Only the overall rating
              and your name are required.
            </Typography>
          </Box>

          {request?.message && (
            <Alert severity="info" variant="outlined" icon={false}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {request.message}
              </Typography>
            </Alert>
          )}

          {problems.length > 0 && (
            <Alert severity="error">
              <AlertTitle>Please check your feedback</AlertTitle>
              {problems.map((problem) => (
                <div key={problem}>{problem}</div>
              ))}
            </Alert>
          )}

          <Stack
            gap={2}
            sx={{
              p: { xs: 2, sm: 2.5 },
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <RatingRow
              label="Overall, how would you rate us?"
              value={overallRating}
              onChange={setOverallRating}
              required
            />
            {(request?.categories ?? []).map(({ key, label }) => (
              <RatingRow
                key={key}
                label={label}
                size="medium"
                value={ratings[key] ?? null}
                onChange={(next) => setRatings((prev) => ({ ...prev, [key]: next ?? undefined }))}
              />
            ))}
          </Stack>

          <Stack gap={1}>
            <Typography fontWeight={700}>Would you book us again?</Typography>
            <ToggleButtonGroup
              exclusive
              value={bookAgain}
              onChange={(_e, next) => setBookAgain(next)}
              size="small"
              color="primary"
            >
              {(request?.bookAgainOptions ?? ['Yes', 'Maybe', 'No']).map((option) => (
                <ToggleButton key={option} value={option} sx={{ px: 3, fontWeight: 700 }}>
                  {option}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="What went well?"
                value={wentWell}
                onChange={(e) => setWentWell(e.target.value.slice(0, 5000))}
                fullWidth
                multiline
                minRows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="What could we improve?"
                value={couldImprove}
                onChange={(e) => setCouldImprove(e.target.value.slice(0, 5000))}
                fullWidth
                multiline
                minRows={3}
                helperText="Please don't include personal details about patients or members of the public."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Your name"
                value={respondentName}
                onChange={(e) => setRespondentName(e.target.value)}
                required
                fullWidth
                autoComplete="name"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Your role (optional)"
                placeholder="e.g. Event Manager"
                value={respondentRole}
                onChange={(e) => setRespondentRole(e.target.value)}
                fullWidth
                autoComplete="organization-title"
              />
            </Grid>
          </Grid>

          {hasComments && (
            <FormControlLabel
              sx={{ alignItems: 'flex-start', ml: 0 }}
              control={
                <Checkbox
                  checked={testimonialConsent}
                  onChange={(e) => setTestimonialConsent(e.target.checked)}
                  sx={{ pt: 0.25 }}
                />
              }
              label={
                <Typography variant="body2">
                  {request?.orgName || 'The team'} may quote my comments (with my name and role)
                  in their marketing and tenders.
                </Typography>
              }
            />
          )}

          <Button
            type="submit"
            color="success"
            variant="contained"
            size="large"
            fullWidth
            disabled={!canSubmit}
          >
            {submitting ? 'Sending…' : 'Send feedback'}
          </Button>

          <Typography variant="caption" color="text.secondary">
            Your feedback goes to {request?.orgName || 'the organisation that sent this link'},
            who use it to review and improve their service. This link is personal to you, can be
            used once and expires on {moment(request?.expires).format('Do MMMM YYYY')}. See the{' '}
            <Link to="/privacy-policy" target="_blank" rel="noreferrer">
              AmbuNet privacy policy
            </Link>{' '}
            for how the platform handles data.
          </Typography>
        </Stack>
      )}
    </CorporateSplitPage>
  )
}

export default EventFeedbackPublic
